import { useEffect, useState } from 'react';
import { financialConfigApi } from '@verlo/shared/services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './Section';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Switch } from '@verlo/shared/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useSite } from '../../../contexts/SiteContext';

const FIELDS = [
  { key: 'float', label: 'Float per till', money: true, help: 'What each drawer opens with.' },
  { key: 'variance_reason_over', label: 'Reason needed over', money: true, help: 'A variance past this needs a reason at cash-off.' },
  { key: 'paid_out_manager_over', label: 'Paid-out needs a manager over', money: true, help: 'A paid-out past this needs a manager\'s PIN.' },
  { key: 'safe_limit', label: 'Safe limit', money: true, help: 'Over this, Today says it\'s time to bank.' },
];

/**
 * Settings › Business › Cash (cash-off wave 2): the business's drawer rules, and the one
 * venue's overrides when a site is picked — blank means "use the business default".
 */
const CashCard = () => {
  const { sites } = useSite();
  const [cfg, setCfg] = useState(null);
  const [site, setSite] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    financialConfigApi.getSettings(undefined).then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  if (!cfg) return null;
  const defaults = { float: 100, variance_reason_over: 5, denominations: true, paid_out_manager_over: 50, safe_limit: 2000, ...(cfg.default_cash || {}) };
  const venue = ((cfg.venues || {})[site] || {}).cash || {};
  const setDefault = (patch) => setCfg({ ...cfg, default_cash: { ...defaults, ...patch } });
  const setVenue = (patch) => setCfg({
    ...cfg,
    venues: { ...(cfg.venues || {}), [site]: { ...((cfg.venues || {})[site] || {}), cash: { ...venue, ...patch } } },
  });

  const num = (v) => (v === '' || v == null ? null : Number(v));
  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        default_cash: {
          float: Number(defaults.float) || 0,
          variance_reason_over: Number(defaults.variance_reason_over) || 0,
          denominations: !!defaults.denominations,
          paid_out_manager_over: Number(defaults.paid_out_manager_over) || 0,
          safe_limit: Number(defaults.safe_limit) || 0,
        },
      };
      if (site) {
        patch.venues = { [site]: { cash: Object.fromEntries(FIELDS.map((f) => [f.key, num(venue[f.key])])) } };
      }
      const { data } = await financialConfigApi.updateSettings(patch);
      setCfg(data);
      toast.success('Cash settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the cash settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="settings-cash">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Cash</CardTitle>
        <CardDescription>The drawer's rules: the float, when a variance needs a reason, when a paid-out needs a manager, how much the safe should hold.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {FIELDS.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label htmlFor={`cash-${f.key}`} className="text-[12.5px] text-ink-soft">{f.label}</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft">£</span>
                <Input id={`cash-${f.key}`} type="number" inputMode="decimal" min="0" step="0.01" className="pl-7 font-mono tabular-nums"
                  value={defaults[f.key] ?? ''} onChange={(e) => setDefault({ [f.key]: e.target.value })} data-testid={`settings-cash-${f.key}`} />
              </div>
              <p className="text-[12px] text-ink-faint">{f.help}</p>
            </div>
          ))}
        </div>
        <label className="flex items-center justify-between gap-3 rounded-md border border-hairline px-3 py-2 text-[13.5px] text-ink">
          Count by denomination at cash-off
          <Switch checked={!!defaults.denominations} onCheckedChange={(v) => setDefault({ denominations: v })} data-testid="settings-cash-denominations" />
        </label>

        {sites.length > 0 && (
          <div className="space-y-3 border-t border-hairline pt-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-[13px] font-semibold text-ink">One venue's own rules</p>
              <Select value={site || 'none'} onValueChange={(v) => setSite(v === 'none' ? '' : v)}>
                <SelectTrigger className="h-9 w-[220px] bg-white" data-testid="settings-cash-site"><SelectValue placeholder="Pick a venue" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No venue override</SelectItem>
                  {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {site && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {FIELDS.map((f) => (
                  <div key={f.key} className="space-y-1">
                    <Label className="text-[12px] text-ink-soft">{f.label}</Label>
                    <Input type="number" inputMode="decimal" min="0" step="0.01" className="font-mono tabular-nums" placeholder={String(defaults[f.key] ?? '')}
                      value={venue[f.key] ?? ''} onChange={(e) => setVenue({ [f.key]: e.target.value })} data-testid={`settings-cash-venue-${f.key}`} />
                  </div>
                ))}
                <p className="col-span-full text-[12px] text-ink-faint">Blank uses the business default.</p>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end">
          <Button className="bg-verlo hover:bg-verlo-hover" onClick={save} disabled={saving} data-testid="settings-cash-save">
            {saving && <Loader2 size={14} className="mr-2 animate-spin" />}Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CashCard;
